import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import EmailAddressField from 'components/fields/EmailAddressField';
import FirstNameField from 'components/fields/FirstNameField';
import LastNameField from 'components/fields/LastNameField';
import PhoneNumberField from 'components/fields/PhoneNumberField';
import UserFormFields from 'components/forms/UserFormFields';

import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';
import useUser from 'hooks/useUser';

import useTranslation from './hooks/useTranslation';

import {
    EMAIL_FIELD_NAME,
    FIRST_NAME_FIELD_NAME,
    LAST_NAME_FIELD_NAME,
    PHONE_FIELD_NAME,
    TEST_ID
} from './constants';
import type { FunctionComponent, Props, UpdateUserFormSchema } from './types';

import styles from './styles.module.scss';

const UpdateUserForm: FunctionComponent<Props> = ({
    className = '',
    formValues,
    isClosing,
    onClose,
    setIsClosing,
    testId = TEST_ID,
    userId
}) => {
    const { assignUserRoles, removeUserRoles } = useUser();
    const {
        EMAIL_ADDRESS_FIELD_PLACEHOLDER,
        FIRST_NAME_FIELD_PLACEHOLDER,
        LAST_NAME_FIELD_PLACEHOLDER,
        PHONE_NUMBER_FIELD_PLACEHOLDER,
        SAVE
    } = useTranslation();

    const form = useForm<UpdateUserFormSchema>({
        defaultValues: {
            ...formValues,
            roles: formValues?.roles?.map(role => ({ label: role, value: role }))
        },
        mode: 'onChange'
    });

    const {
        control,
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose: () => setIsClosing?.(false),
        onClose,
        requestingClose: isClosing
    });

    const onSubmit = async (data: UpdateUserFormSchema) => {
        if (!isDirty) {
            onClose?.();

            return;
        }

        const originalRoles = formValues?.roles ?? [];
        const submittedRoles = data.roles.map(role => role.value);

        const rolesToAssign = submittedRoles.filter(role => !originalRoles.includes(role));
        const rolesToRemove = originalRoles.filter(role => !submittedRoles.includes(role));

        const results = await Promise.all([
            rolesToAssign.length > 0
                ? assignUserRoles({ id: userId, roleAssignment: { roles: rolesToAssign } })
                : true,
            rolesToRemove.length > 0
                ? removeUserRoles({ id: userId, roleAssignment: { roles: rolesToRemove } })
                : true
        ]);

        if (results.every(Boolean)) {
            onClose?.();
        }
    };

    return (
        <VerticalContainer className={`${styles.updateUserForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form form={form}>
                    <UserFormFields>
                        <FirstNameField
                            control={control}
                            isDisabled={true}
                            name={FIRST_NAME_FIELD_NAME}
                            placeholder={FIRST_NAME_FIELD_PLACEHOLDER}
                        />

                        <LastNameField
                            control={control}
                            isDisabled={true}
                            name={LAST_NAME_FIELD_NAME}
                            placeholder={LAST_NAME_FIELD_PLACEHOLDER}
                        />

                        <EmailAddressField
                            control={control}
                            isDisabled={true}
                            name={EMAIL_FIELD_NAME}
                            placeholder={EMAIL_ADDRESS_FIELD_PLACEHOLDER}
                        />

                        <PhoneNumberField
                            control={control}
                            isDisabled={true}
                            name={PHONE_FIELD_NAME}
                            placeholder={PHONE_NUMBER_FIELD_PLACEHOLDER}
                        />
                    </UserFormFields>

                    <FormSubmitPanel
                        buttonText={SAVE}
                        className={styles.formSubmitPanel}
                        errors={errors}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default UpdateUserForm;
